import { action, makeObservable, observable, runInAction } from 'mobx';
import 'firebase/compat/auth';
import 'firebase/compat/functions';
import 'firebase/compat/firestore';
import firebase from 'firebase/compat/app';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { AxiosError } from 'axios';
import { getUserCompanies, getUserPermissions } from '../api/admin';
import UserCompany from '../model/UserCompany';
import {
  addToResellerCompanies,
  addToUserCompanies,
  removeFromResellerCompanies,
  removeFromUserCompanies,
} from '../api';
import Reseller from '../model/reseller';

export enum LoginState {
  LOGGED_OUT,
  LOADING,
  LOADING_PERMISSION,
  RESELLER,
  LOGGED_IN,
  INSUFFICIENT_PERMISSIONS,
}

class AuthStore {
  user: firebase.User | null = null;

  state: LoginState = LoginState.LOADING;

  loading = false;

  userCompanies: UserCompany[] = [];

  isAdmin = false;

  isReseller = false;

  reseller: Reseller | null = null;

  userCompanyLoading = false;

  constructor() {
    makeObservable(this, {
      user: observable,
      userCompanies: observable,
      loading: observable,
      state: observable,
      logIn: action,
      handleStateChange: action,
      isAdmin: observable,
      isReseller: observable,
      reseller: observable,
      userCompanyLoading: observable,
    });

    firebase.auth().onAuthStateChanged((user) => {
      this.handleStateChange(user);
    });
  }

  async logIn(email: string, password: string): Promise<void> {
    const cleanEmail = email?.trim();
    const cleanPassword = password?.trim();

    await firebase.auth().signInWithEmailAndPassword(cleanEmail, cleanPassword);
  }

  async logOut(): Promise<void> {
    await firebase
      .auth()
      .signOut()
      .then(() => this.resetUser());
  }

  getCompanies = async (): Promise<void> => {
    if (this.isAdmin || this.isReseller) {
      const res = await getUserCompanies();
      runInAction(() => {
        this.userCompanies = res;
        this.loading = false;
      });
    }
  };

  async handleStateChange(user: firebase.User | null): Promise<void> {
    if (user) {
      this.state = LoginState.LOADING_PERMISSION;
      const userPermission = await getUserPermissions(user.uid).catch((e) => {
        if (e instanceof AxiosError && e.response?.status === 401) {
          this.state = LoginState.LOGGED_OUT;
          this.user = null;
          firebase.auth().signOut();
        }
        throw e;
      });

      if (userPermission.isAdmin) {
        runInAction(() => {
          this.user = user;
          this.isAdmin = userPermission.isAdmin;
          this.isReseller = userPermission.isReseller;
          this.reseller = userPermission.reseller;
          this.state = LoginState.LOGGED_IN;
        });
        this.getCompanies();
      } else if (userPermission.isReseller) {
        runInAction(() => {
          this.user = user;
          this.isAdmin = false;
          this.isReseller = userPermission.isReseller;
          this.reseller = userPermission.reseller;
          this.state = LoginState.RESELLER;
          this.getCompanies();
        });
      } else {
        runInAction(() => {
          this.user = user;
          this.state = LoginState.INSUFFICIENT_PERMISSIONS;
        });
      }
    } else {
      this.state = LoginState.LOGGED_OUT;
      this.user = null;
    }
  }

  async addToUserCompanies(companyKey: string): Promise<void> {
    if (companyKey) {
      const uid = this.user ? this.user.uid : '';
      const resellerId = this.reseller ? this.reseller.id : '';
      if (this.isReseller) {
        await addToResellerCompanies(resellerId, companyKey);
      } else if (this.isAdmin) {
        await addToUserCompanies(uid, companyKey);
      }
      await this.getCompanies();
    }
  }

  async removeFromUserCompanies(companyKey: string): Promise<void> {
    if (companyKey) {
      const uid = this.user ? this.user.uid : '';
      const resellerId = this.reseller ? this.reseller.id : '';
      if (this.isReseller) {
        await removeFromResellerCompanies(resellerId, companyKey);
      } else if (this.isAdmin) {
        await removeFromUserCompanies(uid, companyKey);
      }
      await this.getCompanies();
    }
  }

  async sendPasswordResetEmail(email: string) {
    if (!email) {
      throw new Error('Email cannot be empty.');
    }
    const auth = getAuth();
    await sendPasswordResetEmail(auth, email);
  }

  resetUser() {
    runInAction(() => {
      this.user = null;
      this.userCompanies = [];
      this.loading = false;
      this.state = LoginState.LOGGED_OUT;
      this.isAdmin = false;
      this.isReseller = false;
      this.reseller = null;
    });
  }
}

export default AuthStore;
